import { useTranslation } from "react-i18next";
import { Popup, TWclassNames } from "../../../utils/Div";
import { OperationCanFinish, OperationsStatus, status } from "../../../Utils";
import {
  CheckBadgeIcon,
  ExclamationCircleIcon,
  MapIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { PMNotice } from "../../../utils/notifications";
import { fetchReportGoogleMapsRoute } from "../../../../api/service/report";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { itemRowResolved } from "../../quality/checklists/checklistResponseConfig";

export const FullReportAddress = (report) => {
  if (report?.delivery_address?.id) {
    const { delivery_address } = report;
    const { address, postal, place } = delivery_address;

    const address2 = delivery_address?.address2
      ? delivery_address?.address2 + ", "
      : "";

    return (
      delivery_address?.address +
      ", " +
      address2 +
      delivery_address?.postal +
      " " +
      delivery_address?.place
    );
  } else {
    return report?.address + ", " + report?.postal + " " + report?.place;
  }
};

export const FullReportTitle = (report) => {
  const { report_number, customer, delivery_address, postal, place } = report;

  const address = delivery_address?.id
    ? delivery_address?.postal + " " + delivery_address?.place
    : postal + " " + place;

  return report.report_number + " - " + customer?.name + " - " + address;
};

export const reportStatuses = {
  draft: { label: "Draft", class: "bg-gray-200 text-black" },
  new: { label: "New", class: "bg-yellow-200 text-green-800" },
  ongoing: { label: "Ongoing", class: "bg-green-100 text-green-800" },
  accomplished: { label: "Accomplished", class: "bg-green-600 text-white" },
  processing: { label: "Processing", class: "bg-yellow-600 text-white" },
  processed: { label: "Processed", class: "bg-blue-600 text-white" },
  closed: { label: "Closed", class: "bg-green-600 text-white" },
  cancelled: { label: "Cancelled", class: "bg-red-600 text-white" },
  ready_invoice: {
    label: "Ready to be invoiced",
    class: "bg-red-600 text-white",
  },
  invoiced: { label: "Invoiced", class: "bg-indigo-600 text-white" },
  paid: { label: "Paid", class: "bg-green-600 text-white" },
};

export const appointmentApprovedStatuses = {
  false: { label: "Agreement not confirmed", class: "bg-red-600 text-white" },
  null: { label: "Agreement not confirmed", class: "bg-red-600 text-white" },
  true: {
    label: "Agreement confirmed",
    class: "bg-green-100 text-green-800",
  },
};

// DO NOT USE - WILL BE REMOVED
export const reportStatusesOptions = [
  // { id: "DRAFT", value: "DRAFT", label: "Draft", title: "Draft" },
  { id: "NEW", value: "NEW", label: "New", title: "New" },
  { id: "ONGOING", value: "ONGOING", label: "Ongoing", title: "Ongoing" },
  { id: "ACCOMPLISHED", value: "ACCOMPLISHED", label: "Accomplished" },
  { id: "PROCESSING", value: "PROCESSING", label: "Processing" },
  { id: "PROCESSED", value: "PROCESSED", label: "Processed" },
  // { id: "CLOSED", value: "CLOSED", label: "Closed" },
  // { id: "CANCELLED", value: "CANCELLED", label: "Cancelled" },
  // {
  //   id: "READY_INVOICE",
  //   value: "READY_INVOICE",
  //   label: "Ready to be invoiced",
  // },
  { id: "INVOICED", value: "INVOICED", label: "Invoiced" },
  // { id: "PAID", value: "PAID", label: "Paid" },
  // Approval statuses
  { id: "APPROVED", value: "APPROVED", label: "Godkjent" },
  { id: "NOT_APPROVED", value: "NOT_APPROVED", label: "Ikke godkjent" },
  { id: "PENDING_APPROVAL", value: "PENDING_APPROVAL", label: "Venter på behandling" },
  { id: "UPDATED", value: "UPDATED", label: "Oppdatert etter behandling" },
];

export const reportViewsOptions = [
  { id: "FINISHED", value: "FINISHED", label: "Ferdig utført" },
  { id: "COMPLAINT", value: "COMPLAINT", label: "Reklamasjon" },
  { id: "MUST_BE_RECTIFIED", value: "MUST_BE_RECTIFIED", label: "Må utbedres" },
  {
    id: "AGREEMENT_NOT_CONFIRMED",
    value: "AGREEMENT_NOT_CONFIRMED",
    label: "Avtale ikke bekreftet",
  },
  { id: "APPROVED", value: "APPROVED", label: "Godkjente" },
  { id: "NOT_APPROVED", value: "NOT_APPROVED", label: "Ikke godkjente" },
];

export const ReportsRouteButton = ({ params }) => {
  // Fetch from API
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const debounceTimer = useRef(null);

  const getRoute = async () => {
    const response = await fetchReportGoogleMapsRoute(params);
    if (response.status === 200) setStatus("success");
    setData(response.data);
  };

  // Return a button if successful

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // set a new timer
    debounceTimer.current = setTimeout(() => {
      getRoute();
    }, 300);

    return () => {
      clearTimeout(debounceTimer.current);
    };
  }, [params]);

  const text =
    "Viser komplett rute for samtlige oppdrag for valgt dag i Google Maps";

  if (status === "success" && data) {
    return (
      <Popup content={text} textSize="md">
        <Link
          target="_blank"
          to={data?.url}
          className="flex bg-blue-400 px-4 py-4 rounded-xl text-white hover:bg-blue-600 mt-2"
        >
          <MapIcon className="h-5 w-5 mr-2" />
          Dagens rute
        </Link>
      </Popup>
    );
  }
};

export function ReportStatus({ report }) {
  const { t } = useTranslation();

  // Determine the status to display - prioritize approval status if report is finished
  let displayStatus = report?.status;
  let statusLabel = null;
  let statusClass = "";

  // If report is finished, check approval status first
  if (report?.finished) {
    if (report?.approved) {
      displayStatus = "approved";
      statusLabel = t("Godkjent");
      statusClass = "bg-green-600 text-white";
    } else if (report?.not_approved) {
      displayStatus = "not_approved";
      statusLabel = t("Ikke godkjent");
      statusClass = "bg-red-600 text-white";
    } else if (report?.updated_after_process) {
      displayStatus = "updated_after_process";
      statusLabel = t("Oppdatert - venter på behandling");
      statusClass = "bg-yellow-600 text-white";
    } else if (!report?.approved && !report?.not_approved) {
      // Finished but not yet processed
      displayStatus = "pending_approval";
      statusLabel = t("Venter på behandling");
      statusClass = "bg-blue-600 text-white";
    }
  }

  // Fall back to regular status if no approval status
  if (!statusLabel && displayStatus) {
    const statusInfo = status[displayStatus];
    if (statusInfo) {
      statusLabel = t(statusInfo.label || "Unknown");
      statusClass = statusInfo.class;
    }
  }

  if (!statusLabel) {
    return null;
  }

  return (
    <span
      className={TWclassNames(
        statusClass,
        "inline-flex rounded-full px-2 text-xs font-semibold leading-5"
      )}
    >
      <span>
        {statusLabel}
        {report?.finished && !report?.updated_after_process && report?.finished_date ? ` - ${report?.finished_date}` : null}
      </span>
    </span>
  );
}

export function ReportInvoiceStatus({ report }) {
  const { t } = useTranslation();

  if (report?.invoiced) {
    return (
      <span
        className={TWclassNames(
          "bg-indigo-600 text-white",
          "inline-flex rounded-full px-2 text-xs font-semibold leading-5"
        )}
      >
        <span>{t("Invoiced")} </span>
      </span>
    );
  }

  return null;
}

export function ReportSimpleAgreementStatus({ isApproved = false }) {
  return (
    <span
      className={TWclassNames(
        isApproved ? "bg-green-100 text-green-800" : "bg-red-200",
        "inline-flex rounded-full px-2 text-xs font-semibold leading-5"
      )}
    >
      {isApproved ? "Bekreftet" : "Ikke bekreftet"}
    </span>
  );
}

export function ReportAppointmentApprovedStatus({ report, type = "normal", className }) {
  const {
    date,
    approved,
    has_agreement,
    confirmed_via_text,
    confirmed_manually,
    confirmed_date,
  } = report?.appointment || {};

  const ConfirmationType = () => {
    confirmed_via_text
      ? " via SMS" + (confirmed_date ? " " + confirmed_date : "")
      : confirmed_manually
      ? " manually"
      : " ?";
  };

  return (
    <span
      className={TWclassNames(
        className,
        approved ? "bg-green-100 text-green-800" : "bg-red-200",
        "inline-flex rounded-full px-2 text-xs font-semibold leading-5"
      )}
    >
      {approved && date ? (
        <>
          Agreement is approved
          {type !== "short" && <ConfirmationType />}
          {} {confirmed_date ? type !== "short" && confirmed_date : null}
        </>
      ) : has_agreement ? (
        "Ikke bekreftet"
      ) : (
        "Ikke avtalt"
      )}
    </span>
  );
}

export function ReportMustBeRectified({ rectify }) {
  if (!rectify) return null;

  return (
    <span
      className={TWclassNames(
        rectify ? "bg-red-200" : "bg-green-100 text-green-800",
        "inline-flex rounded-full px-2 text-xs font-semibold leading-5"
      )}
    >
      {rectify ? "Must be rectified" : null}
    </span>
  );
}

export function ReportAgreementVerifier({ children, report }) {
  const Error = () => (
    <div className="mt-10">
      You cannot create a new agreement yet. The following is missing:
      <ul className="text-red-500 mt-5 list-disc ml-5">
        {!report?.address && <li>Address</li>}
        {!report?.report_type?.id && <li>Report Type</li>}
      </ul>
    </div>
  );

  if (report?.address && report?.report_type?.id) return children;
  else {
    return <Error />;
  }
}

export function ReportAgreementVerifier2({ children, report }) {
  const Error = () => (
    <div className="mt-10">
      You cannot create a new agreement yet. The following is missing:
      <div>
        <OperationsStatus
          operations={report?.operations}
          operationName="create_agreement"
          title="Address"
        />
      </div>
    </div>
  );

  const canFinish = OperationCanFinish({
    operations: report?.operations,
    operationName: "create_agreement",
  });

  if (canFinish) return children;
  else return <Error />;
}

export function ServiceReportOperationObjectStatus({ operations, objectName }) {
  // Return status based on operation?.status from API
  const filteredOperations = operations?.filter(
    (operation) => operation?.object === objectName
  );

  if (filteredOperations?.length === 0) return "NOT_REQUIRED";

  // Use the status from API directly
  const operation = filteredOperations[0];
  return operation?.status?.toUpperCase() || "NOT_FINISHED";
}

/**
 * Calculate checklist progress: any resolved outcome counts (approved, deviation, N/A, performed, or response_code).
 * @param {Object} checklist - The checklist object with items array
 * @returns {Object} - Status object with { isCompleted, statusText, completedCount, totalCount }
 */
export function getChecklistStatus(checklist) {
  // If no checklist exists
  if (!checklist || !checklist.id) {
    return {
      isCompleted: false,
      statusText: "Velg type",
      completedCount: 0,
      totalCount: 0,
      isRequired: false,
    };
  }

  const items = checklist.items || [];
  const totalCount = items.length;

  // If no items exist
  if (totalCount === 0) {
    return {
      isCompleted: false,
      statusText: "Ingen punkter",
      completedCount: 0,
      totalCount: 0,
      isRequired: true,
    };
  }

  const completedCount = items.filter((item) => itemRowResolved(item)).length;

  // Check if all items are completed
  const isCompleted = completedCount === totalCount && totalCount > 0;

  // Determine status text
  let statusText = "Mangler";
  if (isCompleted) {
    statusText = "Ferdig";
  } else if (completedCount > 0) {
    statusText = `${completedCount}/${totalCount} fullført`;
  }

  return {
    isCompleted,
    statusText,
    completedCount,
    totalCount,
    isRequired: true,
  };
}

export function ServiceReportOperationStatus({
  operations,
  operationName,
  title,
}) {
  const { t } = useTranslation();

  // Support both single operation name (string) and multiple operation names (array)
  const operationNames = Array.isArray(operationName) ? operationName : [operationName];

  // filter operations by operation name(s)
  const filteredOperations = operations?.filter(
    (operation) => operationNames.includes(operation?.message)
  );

  function setCanBeCompleted() {
    // Check if any filteredOperations has can_be_completed set to false
    const canBeCompleted = filteredOperations?.every(
      (operation) => operation?.can_be_completed === true
    );

    // return false if any operation has can_be_completed set to false
    return canBeCompleted;
  }

  function icon() {
    if (filteredOperations.length === 0)
      return <CheckBadgeIcon className="h-5 w-5 mr-2" />;

    if (setCanBeCompleted())
      return <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />;

    return <ExclamationCircleIcon className="h-5 w-5 mr-2" />;
  }

  function description() {
    if (filteredOperations?.length === 0) return "Alt ser bra ut!";

    if (setCanBeCompleted()) return "Rapporten kan ferdigstilles uten";

    return "Rapporten kan ikke ferdigstilles uten";
  }

  return (
    <div className="w-full">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">{icon()}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
            <div
              className={TWclassNames(
                filteredOperations && filteredOperations?.length > 0
                  ? setCanBeCompleted()
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
                  : "bg-green-100 text-green-800",
                "px-2 py-1 rounded-full text-xs font-medium"
              )}
            >
              {filteredOperations && filteredOperations?.length > 0
                ? setCanBeCompleted()
                  ? "Ikke påkrevd"
                  : "Mangler"
                : "OK"}
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">{description()}</p>
        </div>
      </div>
    </div>
  );
}

export function ServiceReportOperationStatusV2({
  operations,
  operationName,
  title,
}) {
  const { t } = useTranslation();

  // filter operations by operation name
  const filteredOperations = operations?.filter(
    (operation) => operation?.message === operationName
  );

  function setCanBeCompleted() {
    // Check if any filteredOperations has can_be_completed set to false
    const canBeCompleted = filteredOperations?.every(
      (operation) => operation?.can_be_completed === true
    );

    // return false if any operation has can_be_completed set to false
    return canBeCompleted;
  }

  function icon() {
    if (filteredOperations.length === 0)
      return <CheckBadgeIcon className="h-5 w-5 mr-2" />;

    if (setCanBeCompleted())
      return <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />;

    return <ExclamationCircleIcon className="h-5 w-5 mr-2" />;
  }

  function description() {
    if (filteredOperations?.length === 0) return "Alt ser bra ut!";

    if (setCanBeCompleted()) return "Rapporten kan ferdigstilles uten";

    return "Rapporten kan ikke ferdigstilles uten";
  }

  return (
    <>
      {operations?.length > 0 &&
        operations.map((operation) => (
          <PMNotice key={operation} title={title} />
        ))}
    </>
  );
}
